import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from '@mui/material';
import { RiExpandUpDownLine, RiAddLine, RiDeleteBinLine } from '@remixicon/react';
import {
  updateElementProperties,
  updateElementPosition,
  updateElementSize,
  addAnimationToElement,
  removeAnimationFromElement,
} from '../redux/slices/canvasSlice';
import { colors, fontSizes } from '../../theme/tokens';
import AnimationModal from './v2/AnimationModal';

const PropertiesPanel = () => {
  const dispatch = useDispatch();
  const { elements, selectedElementId } = useSelector((state) => state.canvas);

  const selectedElement = elements.find((el) => el.id === selectedElementId);
  const { properties, position, size, type, animations = [] } = selectedElement;

  const [animationModalOpen, setAnimationModalOpen] = useState(false);

  const primaryAnimation = useMemo(
    () => (animations && animations.length > 0 ? animations[0] : null),
    [animations]
  );

  if (!selectedElement) {
    return (
      <Paper
        elevation={2}
        sx={{
          width: 320,
          height: '100%',
          padding: 3,
          overflow: 'auto',
          backgroundColor: 'white',
        }}
      >
        <Typography variant="h6" gutterBottom>
          Properties
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select an element to edit its properties
        </Typography>
      </Paper>
    );
  }


  const handlePropertyChange = (key, value) => {
    dispatch(
      updateElementProperties({
        id: selectedElementId,
        properties: { [key]: value },
      })
    );
  };

  const handlePositionChange = (axis, value) => {
    dispatch(
      updateElementPosition({
        id: selectedElementId,
        position: { ...position, [axis]: parseFloat(value) || 0 },
      })
    );
  };

  const handleSizeChange = (dimension, value) => {
    dispatch(
      updateElementSize({
        id: selectedElementId,
        size: { ...size, [dimension]: parseFloat(value) || 0 },
      })
    );
  };

  const handleOpenAnimationModal = () => {
    setAnimationModalOpen(true);
  };

  const handleCloseAnimationModal = () => {
    setAnimationModalOpen(false);
  };

  const handleSaveAnimation = (config) => {
    if (primaryAnimation) {
      // Replace the existing primary animation with updated config
      dispatch(
        removeAnimationFromElement({
          elementId: selectedElementId,
          animationId: primaryAnimation.id,
        })
      );
    }
    dispatch(
      addAnimationToElement({
        elementId: selectedElementId,
        animation: config,
      })
    );
  };

  const handleRemovePrimaryAnimation = () => {
    if (!primaryAnimation) return;
    dispatch(
      removeAnimationFromElement({
        elementId: selectedElementId,
        animationId: primaryAnimation.id,
      })
    );
  };

  const fontFamilies = ['Outfit', 'Bricolage Grotesque', 'Inter', 'Arial', 'Times New Roman'];

  return (
    <Paper
      elevation={2}
      sx={{
        width: 320,
        height: '100%',
        overflow: 'auto',
        backgroundColor: 'white',
      }}
    >
      <Box sx={{ padding: 2 }}>
        <Typography variant="h6" gutterBottom>
          Properties
        </Typography>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          {type.toUpperCase()} Element
        </Typography>
      </Box>

      <Divider />

      {/* Position & Size */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<RiExpandUpDownLine size={20} />}>
          <Typography variant="subtitle2">Position & Size</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', gap: 1, marginBottom: 2 }}>
            <TextField
              label="X"
              type="number"
              size="small"
              value={position.x}
              onChange={(e) => handlePositionChange('x', e.target.value)}
              fullWidth
            />
            <TextField
              label="Y"
              type="number"
              size="small"
              value={position.y}
              onChange={(e) => handlePositionChange('y', e.target.value)}
              fullWidth
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Width"
              type="number"
              size="small"
              value={size.width}
              onChange={(e) => handleSizeChange('width', e.target.value)}
              fullWidth
            />
            <TextField
              label="Height"
              type="number"
              size="small"
              value={size.height}
              onChange={(e) => handleSizeChange('height', e.target.value)}
              fullWidth
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Text Properties */}
      {type === 'text' && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<RiExpandUpDownLine size={20} />}>
            <Typography variant="subtitle2">Text</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              label="Text Content"
              multiline
              rows={3}
              value={properties.text || ''}
              onChange={(e) => handlePropertyChange('text', e.target.value)}
              fullWidth
              sx={{ marginBottom: 2 }}
            />

            <FormControl fullWidth sx={{ marginBottom: 2 }}>
              <InputLabel>Font Family</InputLabel>
              <Select
                value={properties.fontFamily || 'Outfit'}
                onChange={(e) => handlePropertyChange('fontFamily', e.target.value)}
                label="Font Family"
                size="small"
              >
                {fontFamilies.map((font) => (
                  <MenuItem key={font} value={font}>
                    {font}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Font Size"
              type="text"
              size="small"
              value={properties.fontSize || '16px'}
              onChange={(e) => handlePropertyChange('fontSize', e.target.value)}
              fullWidth
              sx={{ marginBottom: 2 }}
            />

            <FormControl fullWidth sx={{ marginBottom: 2 }}>
              <InputLabel>Font Weight</InputLabel>
              <Select
                value={properties.fontWeight || 400}
                onChange={(e) => handlePropertyChange('fontWeight', e.target.value)}
                label="Font Weight"
                size="small"
              >
                <MenuItem value={300}>Light</MenuItem>
                <MenuItem value={400}>Regular</MenuItem>
                <MenuItem value={500}>Medium</MenuItem>
                <MenuItem value={600}>Semi Bold</MenuItem>
                <MenuItem value={700}>Bold</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Text Color"
              type="color"
              size="small"
              value={properties.color || '#000000'}
              onChange={(e) => handlePropertyChange('color', e.target.value)}
              fullWidth
              sx={{ marginBottom: 2 }}
            />

            <FormControl fullWidth>
              <InputLabel>Text Align</InputLabel>
              <Select
                value={properties.textAlign || 'left'}
                onChange={(e) => handlePropertyChange('textAlign', e.target.value)}
                label="Text Align"
                size="small"
              >
                <MenuItem value="left">Left</MenuItem>
                <MenuItem value="center">Center</MenuItem>
                <MenuItem value="right">Right</MenuItem>
              </Select>
            </FormControl>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Image Properties */}
      {type === 'image' && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<RiExpandUpDownLine size={20} />}>
            <Typography variant="subtitle2">Image</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              label="Image URL"
              value={properties.src || ''}
              onChange={(e) => handlePropertyChange('src', e.target.value)}
              fullWidth
              sx={{ marginBottom: 2 }}
            />

            <FormControl fullWidth>
              <InputLabel>Object Fit</InputLabel>
              <Select
                value={properties.objectFit || 'cover'}
                onChange={(e) => handlePropertyChange('objectFit', e.target.value)}
                label="Object Fit"
                size="small"
              >
                <MenuItem value="cover">Cover</MenuItem>
                <MenuItem value="contain">Contain</MenuItem>
                <MenuItem value="fill">Fill</MenuItem>
                <MenuItem value="none">None</MenuItem>
              </Select>
            </FormControl>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Style Properties */}
      <Accordion>
        <AccordionSummary expandIcon={<RiExpandUpDownLine size={20} />}>
          <Typography variant="subtitle2">Style</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TextField
            label="Background Color"
            type="color"
            size="small"
            value={properties.backgroundColor || '#FFFFFF'}
            onChange={(e) => handlePropertyChange('backgroundColor', e.target.value)}
            fullWidth
            sx={{ marginBottom: 2 }}
          />

          <Typography variant="caption" gutterBottom>
            Border Radius
          </Typography>
          <Slider
            value={properties.borderRadius || 0}
            onChange={(e, value) => handlePropertyChange('borderRadius', value)}
            min={0}
            max={50}
            valueLabelDisplay="auto"
            sx={{ marginBottom: 2 }}
          />

          <Typography variant="caption" gutterBottom>
            Opacity
          </Typography>
          <Slider
            value={properties.opacity !== undefined ? properties.opacity : 1}
            onChange={(e, value) => handlePropertyChange('opacity', value)}
            min={0}
            max={1}
            step={0.1}
            valueLabelDisplay="auto"
          />
        </AccordionDetails>
      </Accordion>

      {/* Animations - simplified to a single primary animation with modal editor */}
      <Accordion>
        <AccordionSummary expandIcon={<RiExpandUpDownLine size={20} />}>
          <Typography variant="subtitle2">Animations</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Header row like design: label + add button */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
            }}
          >
            <Typography variant="subtitle2">Animation</Typography>
            <IconButton
              size="small"
              onClick={handleOpenAnimationModal}
              sx={{
                borderRadius: '50%',
                border: '1px solid #E5E7EB',
                width: 28,
                height: 28,
              }}
            >
              <RiAddLine size={18} />
            </IconButton>
          </Box>

          {/* Empty state (first image) */}
          {!primaryAnimation && (
            <Typography variant="body2" color="text.secondary">
              No animation selected
            </Typography>
          )}

          {/* Selected animation pill (second image style) */}
          {primaryAnimation && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#F5F5F8',
                borderRadius: 2,
                px: 1.5,
                py: 1,
                mt: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.5,
                    backgroundColor: '#EEE5FF',
                  }}
                />
                <Box>
                  <Typography variant="body2">
                    {primaryAnimation.type || 'Animation'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {primaryAnimation.duration || 1000} ms
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleOpenAnimationModal}
                  sx={{ textTransform: 'none', fontSize: 12 }}
                >
                  Edit
                </Button>
                <IconButton
                  size="small"
                  onClick={handleRemovePrimaryAnimation}
                  sx={{ ml: 0.5 }}
                >
                  <RiDeleteBinLine size={16} />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* Modal that matches third image (tabs + controls) */}
          <AnimationModal
            open={animationModalOpen}
            onClose={handleCloseAnimationModal}
            initialAnimation={primaryAnimation}
            onSave={handleSaveAnimation}
          />
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default PropertiesPanel;



